import type { VpIconName } from '@vtmn-play/icons'
import { VpButton, VpIcon } from '@vtmn-play/react'
import { useState } from 'react'

interface AlertProps {
  icon: IconOptions
  text: string
  severity: 'positive' | 'negative' | 'warning' | 'info'
  action?: ActionOptions
  closable?: boolean | ClosableOptions
}

export interface IconOptions {
  name: VpIconName
  style: 'LINE' | 'IN_A_CIRCLE' | 'FILL' | 'COLORED'
}

export interface ActionOptions {
  label: string
  doOnClick: () => void
}

export interface ClosableOptions {
  onClose?: () => void
}

/**
 styles:
 LINE -> icon as a drawn line
 COLORED -> icon as a colored drawn line
 FILL -> icon as an invisible drawn line with a colorful filled background (the background is a part of the icon)
 ex: fits icons surrounded by a circle
 IN_A_CIRCLE -> icon as an invisible drawn line included in a colorful circle that is not part of the icon
 ex: fits icons not originally surrounded by a circle
 */
const getIconStatusClassName = (icon: IconOptions, severity: 'positive' | 'negative' | 'warning' | 'info') => {
  if (icon?.style === 'FILL') return `mg-vp-alert-icon-fill--${severity}`
  if (icon?.style === 'COLORED') return `mg-vp-alert-icon-colored--${severity}`
  if (icon?.style === 'IN_A_CIRCLE') return `mg-vp-alert-icon-circle--${severity}`
  return ''
}

const getIconSize = (icon: IconOptions) => {
  if (icon?.style === 'FILL') return '22'
  return '16'
}

const Alert = ({ icon, text, severity, action, closable }: AlertProps) => {
  const [isVisible, setIsVisible] = useState(true)

  const handleClose = () => {
    if (typeof closable === 'object') closable.onClose?.()
    setIsVisible(false)
  }

  return (
    <>
      {isVisible ? (
        <div data-testid="mg-vp-alert" className={`mg-vp-alert mg-vp-alert-${severity} vp-body-s`}>
          <div data-testid="mg-vp-alert-icon" className={`mg-vp-alert-icon ${getIconStatusClassName(icon, severity)}`}>
            <VpIcon {...{ name: icon.name, size: getIconSize(icon) }} />
          </div>
          <div data-testid="mg-vp-alert-text" className="mg-vp-alert-text">
            {text}
          </div>
          {action ? (
            <VpButton
              data-testid="mg-vp-alert-action-button"
              variant="primary"
              variantStyle="regular"
              shape="rounded"
              size="small"
              onClick={() => {
                action.doOnClick()
              }}
            >
              {action.label}
            </VpButton>
          ) : null}
          {closable ? (
            <button
              type="button"
              data-testid="mg-vp-alert-close-button"
              onClick={handleClose}
              className="mg-vp-alert-close-button"
            >
              <VpIcon {...{ name: 'close', size: 18 }} />
            </button>
          ) : null}
        </div>
      ) : null}
    </>
  )
}

export default Alert
